// Persists an in-progress "Create Podcast" request for logged-out (guest) users
// so it survives the full-page Google OAuth redirect and can be resumed after login.
//
// - Serializable form fields + guest team go in localStorage.
// - Box-score File objects can't be JSON-serialized, so they're stored in
//   IndexedDB (which supports structured cloning of File/Blob) keyed by game uid.

const DRAFT_KEY = 'pgb_guest_draft';
const DB_NAME = 'pgb-guest';
const STORE = 'files';

// Sentinel used as formData.selectedTeamId for a guest-entered team that hasn't
// been saved to the database yet.
export const GUEST_TEAM_ID = '__guest__';

function hasIndexedDb() {
  return typeof indexedDB !== 'undefined';
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbGet(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbClearAll() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Save the guest's in-progress request. Files are stripped from the JSON payload
// and stashed in IndexedDB, keyed by each game's uid.
export async function saveGuestDraft(formData, guestTeam) {
  const games = (formData.games || []).map((g) => {
    const files = Array.isArray(g.boxScores) ? g.boxScores : [];
    return { game: g, files };
  });

  // Persist files to IndexedDB (best-effort; text-only drafts still work).
  if (hasIndexedDb()) {
    try {
      for (const { game, files } of games) {
        if (files.length > 0 && game.uid) {
          await idbSet(game.uid, files);
        }
      }
    } catch (err) {
      console.error('[guestDraft] Failed to persist files to IndexedDB:', err);
    }
  }

  const strippedFormData = {
    ...formData,
    games: (formData.games || []).map((g) => ({ ...g, boxScores: [] })),
  };

  try {
    localStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({ formData: strippedFormData, guestTeam: guestTeam || null, savedAt: Date.now() })
    );
  } catch (err) {
    console.error('[guestDraft] Failed to persist draft to localStorage:', err);
  }
}

// Returns { formData, guestTeam } with box-score files rehydrated, or null.
export async function loadGuestDraft() {
  let raw = null;
  try {
    raw = localStorage.getItem(DRAFT_KEY);
  } catch (err) {
    return null;
  }
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    return null;
  }
  if (!parsed || !parsed.formData) return null;

  const formData = parsed.formData;
  if (hasIndexedDb() && Array.isArray(formData.games)) {
    for (const game of formData.games) {
      if (game.uid) {
        try {
          const files = await idbGet(game.uid);
          game.boxScores = Array.isArray(files) ? files : [];
        } catch (err) {
          game.boxScores = [];
        }
      } else {
        game.boxScores = game.boxScores || [];
      }
    }
  }

  return { formData, guestTeam: parsed.guestTeam || null };
}

export function hasGuestDraft() {
  try {
    return !!localStorage.getItem(DRAFT_KEY);
  } catch (err) {
    return false;
  }
}

export async function clearGuestDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch (err) {
    /* no-op */
  }
  if (hasIndexedDb()) {
    try {
      await idbClearAll();
    } catch (err) {
      /* no-op */
    }
  }
}
